import React, { useState, useMemo } from "react";
import { MessageSquare, Heart, ExternalLink, ChevronRight, Filter } from "lucide-react";
import { SeverityBadge, CategoryTag } from "./ui";

export default function FrictionFeed({ signals, onSelect }) {
  const [sev, setSev] = useState("all");
  const [cat, setCat] = useState("all");

  const categories = useMemo(() => {
    const s = new Set(signals.map((x) => x.error_category));
    return ["all", ...Array.from(s)];
  }, [signals]);

  const filtered = signals.filter(
    (s) => (sev === "all" || s.severity === sev) && (cat === "all" || s.error_category === cat)
  );

  return (
    <div className="bg-card border border-border">
      <div className="flex items-center justify-between flex-wrap gap-3 p-4 border-b border-border">
        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground">
          <span className="w-2 h-2 bg-primary live-dot" /> Friction feed · {filtered.length} signals
        </div>
        <div className="flex items-center gap-2">
          <Filter size={13} className="text-muted-foreground" />
          <select value={sev} onChange={(e) => setSev(e.target.value)} data-testid="feed-filter-severity"
            className="font-mono text-xs bg-background border border-border px-2 py-1.5 focus:outline-none focus:border-foreground">
            {["all", "critical", "high", "medium", "low"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="feed-filter-category"
            className="font-mono text-xs bg-background border border-border px-2 py-1.5 max-w-[180px] focus:outline-none focus:border-foreground">
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="p-10 text-center font-mono text-sm text-muted-foreground">No signals match these filters.</div>
      ) : (
        <div className="divide-y divide-border">
          {filtered.map((s) => (
            <button key={s.id} onClick={() => onSelect(s.id)} data-testid={`feed-row-${s.issue_number}`}
              className="w-full text-left px-4 py-4 hover:bg-muted/60 transition-colors flex items-start gap-4 group">
              <div className="pt-0.5"><SeverityBadge severity={s.severity} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <CategoryTag category={s.error_category} />
                  <span className="font-mono text-[11px] text-muted-foreground truncate">{s.repo_full_name} #{s.issue_number}</span>
                </div>
                <div className="font-mono text-sm font-medium truncate">{s.title}</div>
                <div className="flex items-center gap-4 mt-2 font-mono text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1"><MessageSquare size={11} /> {s.comments}</span>
                  <span className="flex items-center gap-1"><Heart size={11} /> {s.reactions}</span>
                  <span>{s.age_days}d old</span>
                  <span className="text-primary font-bold">friction {s.friction_score}</span>
                  <span className="flex items-center gap-1 text-secondary">{s.estimated_hours_lost}h lost</span>
                </div>
              </div>
              <ChevronRight size={18} className="text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition-all mt-1" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
